import React, {Fragment, useEffect, useRef, useState} from "react";
import {isStockable} from "../stockable";
import {useRouter} from "next/router";
import {getPurOrg, uuidv4} from "../../../utils";
import {preparePaiement, navigateToPaiement} from "../../../services/paiementLauncher";
import {Alert, Button, ConfigProvider, Form, Input, InputNumber, Modal, Radio, Result, Select, Space, Typography, notification} from "antd";
import ProductsCardModal from "./products-card-modal";
import {getCurrencyFormater, getDeviseSymbol} from "../../../i18n";
import COUNTRY from "country-json/src/country-by-abbreviation.json";
import ExtraFields from "../../../components/paiement/ExtraFields";
import ApiService from "../../../services/ApiService";
import PhoneInput from "../../../components/WebEditor/components/PhoneInput";
import {t} from "@lingui/core/macro"
import {Trans} from "@lingui/react/macro"
import {getLayoutDirection} from "../../../locales";
import {getPrimaryColor} from "../tokens";
import logger from "../../../services/logger"
import {getCampagneCurrencyOptions, getProductPrice, onCampagneDeviseChange, readCampagneDevise, storeCampagneDevise} from "./devise-choice";
import {resumeProduct, stockOf, targetWithTeam} from "../V2/model";

const {Title}=Typography

export default function ModalDon({context,campagneData,campagneTarget,getRef,noModal})
{
    let _defaultSelectedProducts=[]
    const principalDevise=(campagneData?.principalDevise || context?.org?.devise || "eur").toLowerCase()
    const currencyOptions=getCampagneCurrencyOptions(context,campagneData)
    const linkDevise=typeof context?.query?.devise==="string"
        ? currencyOptions.find(c=>c.devise===context.query.devise.toLowerCase())?.devise : null
    const initialCurrency=currencyOptions.find(c=>c.devise===linkDevise) || currencyOptions[0]
    const resumeAmount=typeof context?.query?.don==="string" ? Number(context.query.don.replace(",",".")) : NaN
    const canResumeAmount=Number.isFinite(resumeAmount) && resumeAmount>0
        && !isStockable(campagneData) && campagneData?.config?.fixedMontant!==true
    const fixedResumeMatches=campagneData?.config?.fixedMontant===true && Number.isFinite(resumeAmount)
        && (!context?.query?.devise || linkDevise)
        ? (campagneData.products || []).filter(p=>Math.abs(
            getProductPrice(p,initialCurrency.devise,principalDevise,initialCurrency.rate,initialCurrency.type)-resumeAmount)<0.005)
        : []
    const resumedLot=isStockable(campagneData) && context?.query?.lot !== undefined && (!context?.query?.devise || linkDevise)
        ? resumeProduct(campagneData.products || [], context.query.lot, context.query.quantite,
            p=>stockOf(campagneData,p.id), resumeAmount,
            p=>getProductPrice(p,initialCurrency.devise,principalDevise,initialCurrency.rate,initialCurrency.type)) : undefined
    if(resumedLot){
        _defaultSelectedProducts=Array(resumedLot.quantity).fill(resumedLot.tierId)
    }else if(fixedResumeMatches.length===1){
        _defaultSelectedProducts=[fixedResumeMatches[0].id]
    }else if(!canResumeAmount && resumedLot===undefined && context?.query?.don===undefined
        && !campagneData?.config?.noDefaultProduct && campagneData?.products?.length){
        _defaultSelectedProducts=[campagneData.products[0].id]
    }

    const [defaultSelectedProducts,setDefaultSelectedProducts]=useState(_defaultSelectedProducts)
    const [loading,setLoading]=useState(false)
    const [submissionError,setSubmissionError]=useState(null)
    const submitting=useRef(false)
    const [visibleModal,setVisibleModal]=useState(false)
    // --- Multi-devises ---
    // Devise principale (implicite, toujours proposée) + devises configurées sur la campagne.
    const [selectedDevise,setSelectedDevise]=useState(linkDevise || principalDevise)
    const router=useRouter()
    const form=useRef(null)
    const productForm=useRef(null)
    // getRef n'est posé qu'au montage : on passe par une ref pour que show() voie la devise courante.
    const showRef=useRef(null)

    const [promesseLink,setPromesseLink]=useState(null)
    const promesseSubmissionId=useRef(uuidv4())

    const {org}=context

    const isMobile=context.isMobile

    // Options du sélecteur de devise : principale + devises de la campagne (taux disponible uniquement).
    const currentEntry=currencyOptions.find(c=>c.devise===selectedDevise) || currencyOptions[0]
    // rate = taux principal -> devise (1 pour la principale)
    const rate=(currentEntry?.type==="PRINCIPAL") ? 1 : (Number(currentEntry?.rate)||1)
    const currencyFormater=getCurrencyFormater(context)

    // Convertit le montant saisi (exprimé dans selectedDevise) en ce qu'on envoie au backend :
    // - PRINCIPALE/VIRTUELLE : le donateur paie en devise principale -> reconversion (montant/rate) + devise=principale.
    // - RÉELLE : on garde le montant + la devise réelle (le backend mappe l'asso encaissante).
    const resolveAmountDevise=(entered)=>{
        const amt=Number(entered)||0
        if(!currentEntry || currentEntry.type==="PRINCIPAL" || currentEntry.type==="VIRTUAL"){
            const principalAmount=(rate && rate!==1) ? Math.round((amt/rate)*100)/100 : amt
            return {montant:principalAmount,devise:principalDevise}
        }
        return {montant:amt,devise:selectedDevise}
    }

    let primaryColor=getPrimaryColor(campagneData.config)

    const productMonth=campagneData?.config?.productMonth || false
    // Durée imposée : seul un nombre l'active. Le réglage vidé depuis l'admin
    // arrive en `null` (la config est fusionnée, jamais purgée) → le donateur
    // doit alors retrouver le choix de la durée, comme si rien n'était réglé.
    const forceMonthNb=typeof campagneData?.config?.forceMonthNb==="number" ? campagneData.config.forceMonthNb : undefined
    // Affichage seulement : un don étalé est présenté par sa mensualité (total ÷ nb de mois).
    const monthlyAmountDisplay=!productMonth && !!campagneData?.config?.monthlyAmountDisplay

    useEffect(() => {
        if(getRef){
            getRef({
                show:(d)=>showRef.current(d)
            })
        }

        if(getStorage()?.getItem("MEM_INFO")){
            try{
                var infos=JSON.parse(getStorage()?.getItem("MEM_INFO"))
                form.current.setFieldsValue(infos)
                form.current.setFieldValue("displayName",(infos.prenom+" "+infos.nom).trim())
            }catch (e){}
        }
    }, []);

    // Le lien de reprise prime sur la mémoire de session, y compris sans fenêtre de choix.
    useEffect(() => {
        if(linkDevise){
            setSelectedDevise(linkDevise)
            storeCampagneDevise(campagneData.id,linkDevise)
            return
        }
        if(context?.query?.don){
            setSelectedDevise(principalDevise)
            storeCampagneDevise(campagneData.id,principalDevise)
            return
        }
        if(!campagneData?.config?.showDeviseModal) return
        const apply=(devise)=>{
            if(devise && currencyOptions.some(c=>c.devise===devise)) setSelectedDevise(devise)
        }
        apply(readCampagneDevise(campagneData.id))
        return onCampagneDeviseChange(campagneData.id,apply)
    }, [campagneData?.id,linkDevise]);


    const getStorage=()=>{
        try {
            return window.localStorage;
        } catch (e) {
            return null;
        }
    }

    // Sélecteur de devise affiché à droite du champ montant (addonAfter).
    // S'il n'y a que la devise principale, on garde le simple symbole (comportement historique).
    const currencyAfterField=()=>{
        if(currencyOptions.length<=1){
            if(productMonth){
                return <Space>{getDeviseSymbol(context)} /<Trans>Mois</Trans></Space>
            }
            return getDeviseSymbol(context)
        }

        return (
            <Space size={4}>
                <Select
                    value={selectedDevise}
                    onChange={(v)=>setSelectedDevise(v)}
                    popupMatchSelectWidth={false}
                    style={{minWidth:90}}
                >
                    {currencyOptions.map(c=>(
                        <Select.Option key={c.devise} value={c.devise}>
                            {getDeviseSymbol(context,c.devise)} {c.devise.toUpperCase()}
                        </Select.Option>
                    ))}
                </Select>
                {productMonth && t`/Mois`}
            </Space>
        )
    }

    // "Reçu fiscal émis par <asso> [drapeau] (Pays) (Charity No …)".
    // Factorisé : utilisé pour la devise principale ET les devises réelles.
    // charityNumber (= rna de l'asso) affiché entre parenthèses dès qu'il est
    // renseigné, quel que soit le pays ("Charity No" reste assez générique).
    // Rendu fluide (inline, pas inline-flex) pour wrapper proprement sur mobile :
    // le nom de l'asso peut revenir à la ligne ; le drapeau colle à son pays
    // (white-space:nowrap sur ce seul groupe) ; le charity number ne se coupe pas.
    const renderReceiptPlace=({assocLabel,country,charityNumber})=>{
        const label=assocLabel || ""
        const ctry=country || ""
        // Nom de pays localisé + drapeau (même mécanique que components/MultiDevise.js).
        const countryName=ctry ? (COUNTRY.find(i=>i.abbreviation===ctry)?.country || ctry) : ""
        const flagCode=ctry ? (ctry.toLowerCase()==="uk" ? "gb" : ctry.toLowerCase()) : null
        const flagSrc=flagCode ? ("https://cdn.jsdelivr.net/gh/lipis/flag-icons/flags/4x3/"+flagCode+".svg") : null
        return (
            <span>
                <b>{label}</b>
                {charityNumber && <span style={{whiteSpace:"nowrap"}}> (Charity No {charityNumber})</span>}
                {(flagSrc || countryName) && (
                    <span style={{whiteSpace:"nowrap"}}>
                        {" "}
                        {flagSrc && <img src={flagSrc} alt={countryName} style={{width:18,height:13,borderRadius:2,objectFit:"cover",verticalAlign:"middle",marginRight:countryName?4:0}}/>}
                        {countryName && <span>({countryName})</span>}
                    </span>
                )}
            </span>
        )
    }

    // Bannière explicative au-dessus du bouton Valider (selon la devise choisie).
    // Toujours affichée (même sans multi-devise / sur la devise principale) : le donateur
    // voit l'asso légale qui émet son reçu fiscal.
    const renderCurrencyBanner=(entered,divide)=>{
        const amt=Number(entered)||0
        if(!currentEntry || amt<=0){
            return null
        }

        const monthSuffix=productMonth ? " "+t`/mois` : ""
        // Don étalé présenté en mensualité : arrondi au supérieur comme le tunnel de paiement.
        const divideNb=monthlyAmountDisplay && Number(divide)>1 ? Number(divide) : 0
        const debit=(value,devise)=>{
            if(!divideNb){
                return currencyFormater(value,2,devise)+monthSuffix
            }
            const nb=divideNb
            const perMonth=currencyFormater(Math.ceil(value/nb),0,devise)
            return t`${perMonth} /mois pendant ${nb} mois`
        }
        // Bannière jaune unique "Vous serez débité de X. Reçu fiscal émis par <asso>".
        const bg="#fff7e6", border="#ffd591"
        let content

        if(currentEntry.type==="REAL"){
            // Encaissement réel dans la devise choisie par l'asso légale de cette devise.
            const receiptPlace=renderReceiptPlace({
                assocLabel:currentEntry.assocLabel,
                country:currentEntry.country,
                charityNumber:currentEntry.charityNumber,
            })
            content=(
                <Trans>
                    Vous serez débité de <b>{debit(amt,selectedDevise)}</b>. Reçu fiscal émis par {receiptPlace}.
                </Trans>
            )
        }else{
            // PRINCIPAL (mono-devise inclus) ET VIRTUAL : le donateur paie en devise
            // PRINCIPALE → encaissement par l'asso principale de la campagne
            // (campagneData.mainAssocInfo). En VIRTUAL on convertit d'abord le montant
            // saisi vers la devise principale + mention "conversion indicative".
            const main=campagneData?.mainAssocInfo
            if(!main || !main.assocLabel){
                // Cas VIRTUAL sans asso résolue : on retombe au moins sur l'info de conversion.
                if(currentEntry.type==="VIRTUAL"){
                    const principalAmount=(rate && rate!==1) ? (amt/rate) : amt
                    content=(
                        <Trans>
                            Montant réellement débité : <b>{debit(principalAmount,principalDevise)}</b> (conversion indicative au taux du jour).
                        </Trans>
                    )
                    return (
                        <div style={{background:"#eef5ff",border:"1px solid #b9d4ff",borderRadius:6,padding:"10px 12px",marginTop:8,fontSize:"0.9rem"}}>
                            {content}
                        </div>
                    )
                }
                return null
            }

            const isVirtual=currentEntry.type==="VIRTUAL"
            const principalAmount=isVirtual ? ((rate && rate!==1) ? (amt/rate) : amt) : amt
            const receiptPlace=renderReceiptPlace(main)
            content=(
                <Fragment>
                    <Trans>
                        Vous serez débité de <b>{debit(principalAmount,principalDevise)}</b>. Reçu fiscal émis par {receiptPlace}.
                    </Trans>
                    {isVirtual && (
                        <div style={{marginTop:4,opacity:0.8}}>
                            <Trans>Conversion indicative au taux du jour.</Trans>
                        </div>
                    )}
                </Fragment>
            )
        }

        return (
            <div style={{background:bg,border:"1px solid "+border,borderRadius:6,padding:"10px 12px",marginTop:8,fontSize:"0.9rem"}}>
                {content}
            </div>
        )
    }

    const show=(_defaultSelectedProducts=[])=>{
        setSubmissionError(null)

        promesseSubmissionId.current=uuidv4()
        setPromesseLink(null)

        if(_defaultSelectedProducts.length>0){
            const p=campagneData.products.find(i=>i.id===_defaultSelectedProducts[0])
            if(p && p.divide){
                // montant exprimé dans la devise sélectionnée (onSubmit reconvertit selon la devise).
                onSubmit({montant:getProductPrice(p,selectedDevise,principalDevise,rate,currentEntry?.type),divide:p.divide,selectedProductId:p.id})
                return false
            }
            setDefaultSelectedProducts(_defaultSelectedProducts)
        }

        setVisibleModal(true)
    }
    showRef.current=show

    const onSubmitPromesse=async (data)=>
    {
        setLoading(true)

        try{

        const campagne={
            ...targetWithTeam(campagneTarget,data.team===undefined ? campagneTarget?.team : data.team),
            theme:campagneData?.config?.theme,
            label:campagneData.label || "",
        }

        if(isStockable(campagneData)){
            let selectedProducts=productForm.current.selectedProducts || []
            if(selectedProducts.length===0){
                // Sans reset, le bouton Valider restait désactivé pour de bon.
                setLoading(false)
                return false
            }

            let products=[]
            for(let sp of selectedProducts){
                let pIndex=products.findIndex(i=>i.id===sp)
                if(pIndex===-1){
                    products.push({id:sp,quantity:1})
                }else{
                    products[pIndex].quantity++
                }
            }

            logger.debug({ products }, "products")
            campagne.products=products
        }

        if(data.displayName){
            campagne.displayName=data.displayName
        }

        const resolvedPromesse=resolveAmountDevise(data.montant)

        let config={
            montant:resolvedPromesse.montant,
            devise:resolvedPromesse.devise,
            idempotencyKey:promesseSubmissionId.current,
            campagne:campagne,
            infos:{
                nom:data.nom,
                prenom:data.prenom,
                email:data.email,
                tel:data.tel,
            }
        }

        logger.debug({ config }, "promesse config")
        const res=await ApiService.post("/campagne/promesse/"+campagneData.id, config)

        if(res && res.success){
            setPromesseLink(res.url)
        }else{
            // Avant : échec silencieux, modale ouverte, rien n'indiquait l'erreur.
            notification.error({message:t`Une erreur est survenue, veuillez réessayer.`})
        }

        }catch(error){
            logger.error({error},"promesse submit failed")
            notification.error({message:t`Une erreur est survenue, veuillez réessayer.`})
        }finally{
            setLoading(false)
        }
    }

    const onSubmit=async (data)=>
    {
        if(submitting.current) return false
        submitting.current=true
        setLoading(true)
        setSubmissionError(null)
        try {
        if(data.montant<1){
            return false
        }

        const campagne={
            ...targetWithTeam(campagneTarget,data.team===undefined ? campagneTarget?.team : data.team),
            theme:campagneData?.config?.secondaryColor,
            label:campagneData.label || "",
            assoc:campagneData?.assoc || "MAIN",
            id:campagneData?.id,
        }

        if(currentEntry?.type==="VIRTUAL"){
            campagne.donorDevise=selectedDevise
            campagne.donorAmount=Number(data.montant)
        }

        let fixedProducts=false
        if(isStockable(campagneData)){
            let selectedProducts=productForm.current.selectedProducts || []
            if(selectedProducts.length===0){
                // Sans reset, le bouton Valider restait désactivé pour de bon.
                setLoading(false)
                return false
            }

            let products=[]
            for(let sp of selectedProducts){
                let pIndex=products.findIndex(i=>i.id===sp)
                if(pIndex===-1){
                    products.push({id:sp,quantity:1})
                }else{
                    products[pIndex].quantity++
                }
            }

            logger.debug({ products }, "products")
            campagne.products=products
            campagne.lockId=uuidv4()
            fixedProducts=true
        }

        var object={
            montant: data.montant,
            divide:data.divide,
            selectedProductId:data.selectedProductId || null,
            maxDivide:campagneData?.config?.maxDivideNB,
            returnUrl: window.location.href,
            organisation:getPurOrg(org),
            type: "CAMPAGNE",
            typeId: campagneData.id,
            cerfa: true,
            campagne:campagne,
            fixedProducts:fixedProducts,
            monthlyAmountDisplay:monthlyAmountDisplay
        }

        if(productMonth)
        {
            object.maxNbMonth=campagneData?.config?.maxNbMonth || 12
            object.minNbMonth=campagneData?.config?.minNbMonth || null
            object.nbMonth=0

            if(forceMonthNb!==undefined){
                object.nbMonth=forceMonthNb
                object.fixedNbMonth=true
            }

            if(campagneData?.config?.defaultNbMonth>1){
                object.nbMonth=campagneData?.config?.defaultNbMonth
            }

            if(data.nbMonth){
                object.nbMonth=data.nbMonth
            }
        }

        // Multi-devises : convertit selon la devise choisie (virtuelle -> principale, réelle -> conservée).
        const resolved=resolveAmountDevise(object.montant)
        object.montant=resolved.montant
        object.devise=resolved.devise

        // Centralisé : pose le payload enrichi (source APP/WEB + returnUrl deep link
        // en app) dans le sessionStorage et renvoie l'URL paiement. On garde le push
        // après le lock tombola éventuel. Cf. services/paiementLauncher.
        const paiementUrl = preparePaiement(object, {org, query: "theme=CAMPAGNE"})

        if(isStockable(campagneData)){
            logger.debug("LOCK DATA")
            let expireAt=new Date()
            expireAt.setMinutes(expireAt.getMinutes()+campagneData?.config?.lockProductMinutes)
            const reservation=await ApiService.post("/campagne/lock/", {
                id:object.campagne.lockId,
                campagne:campagneData.id,
                data:object.campagne,
                expireAt:expireAt,
            })
            if(!reservation?.success) throw new Error("RESERVATION_UNAVAILABLE")
        }

        // La réservation des lots a été attendue juste avant : le geste est
        // consommé, d'où navigateToPaiement (qui délègue au site hôte en embed).
        navigateToPaiement(paiementUrl, router)
        } catch(error) {
            logger.error({error},"donation reservation failed")
            setSubmissionError(t`Une erreur est survenue, veuillez réessayer.`)
            return false
        } finally {
            submitting.current=false
            setLoading(false)
        }
    }

    let Container=Modal
    let containerExtraProps={rootClassName:"modal-don-root"}
    if(noModal){
        Container=ContainerNoModal
        containerExtraProps={}
    }

    // Confirmation de promesse : rendue DANS la modale. Un `return <Result/>` ici
    // sortait du <Container> : la modale se fermait et le résultat s'affichait là où
    // <ModalDon> est monté, sous le footer, hors écran.
    const promesseConfirmation=promesseLink ? (
        <Result
            status="success"
            title={t`Merci pour votre promesse de don !`}
            subTitle={<Fragment><Trans>Vous pouvez honorer votre promesse de don ultérieurement au travers de ce lien :</Trans> <a href={promesseLink} target="_blank">{promesseLink}</a></Fragment>}
            extra={[
                <Button key="new" onClick={()=>{promesseSubmissionId.current=uuidv4();setPromesseLink(null)}}><Trans>Faire un nouveau don</Trans></Button>,
            ]}
        />
    ) : null

    let initialValues={typeMethod:"PAIEMENT",montant: 1, divide: 0,nbMonth:0, ...campagneTarget}
    if(canResumeAmount && (!context?.query?.devise || linkDevise)) initialValues.montant=resumeAmount

    if(forceMonthNb!==undefined){
        initialValues.nbMonth=forceMonthNb
        initialValues.montant=0
    }

    if(campagneData?.config?.defaultNbMonth>1){
        initialValues.nbMonth=campagneData?.config?.defaultNbMonth
    }

    return(
        <ConfigProvider
            direction={getLayoutDirection(context.langValue)}
            theme={{
                components: {
                    Modal: {
                        borderRadius:0
                    },
                    Radio: {
                        buttonColor:primaryColor
                    }
                },
                token: {
                    borderRadiusLG:5,
                    borderRadiusSM:5,
                    colorPrimary:primaryColor
                }
            }}
        >
            <Container destroyOnClose id={"modal-don"} {...containerExtraProps} title={<Title level={4} className="modal-submit-title"><Trans>DONNEZ MAINTENANT</Trans></Title>} footer={false} open={visibleModal} width={context.isMobile?"100%":750} onCancel={()=>setVisibleModal(false)}>
                <div style={{marginTop:40}}>

                    {promesseConfirmation}

                    {!promesseConfirmation && (
                    <Form
                        disabled={loading}
                        ref={(ref) => form.current = ref}
                        initialValues={initialValues}
                        layout={"vertical"}
                        onFinish={(data) => {
                            logger.debug({ data }, "modal-don onFinish")
                            if(!data.typeMethod || data.typeMethod === "PAIEMENT") {
                                onSubmit(data)
                            }

                            if(data.typeMethod === "PROMESSE") {
                                onSubmitPromesse(data)
                            }
                        }}
                    >
                        {submissionError && <Alert type="error" showIcon message={submissionError} style={{marginBlockEnd:20}}/>}
                        <Form.Item noStyle shouldUpdate>
                            {(e)=> {

                                let maxDivide=campagneData?.config?.maxDivideNB || 12
                                let maxNbMonth=campagneData?.config?.maxNbMonth || 12
                                let minNbMonth=campagneData?.config?.minNbMonth || null

                                const montant = e.getFieldValue("montant")
                                const divide = e.getFieldValue("divide")
                                const nbMonth = e.getFieldValue("nbMonth")

                                if(divide && montant<maxDivide) {
                                    e.setFieldValue("divide",0)
                                }

                                return (
                                    <Fragment>

                                        <ProductsCardModal
                                            getRef={(ref)=>productForm.current=ref}
                                            context={context}
                                            campagneData={campagneData}
                                            formRef={form}
                                            defaultSelectedProducts={defaultSelectedProducts}
                                            initialFreeAmount={canResumeAmount && (!context?.query?.devise || linkDevise) ? resumeAmount : null}
                                            divide={divide}
                                            selectedDevise={selectedDevise}
                                            rate={rate}
                                            deviseType={currentEntry?.type}
                                            principalDevise={principalDevise}
                                            forceMonth={productMonth}
                                            nbMonth={nbMonth}
                                            onSubmit={onSubmit}
                                        />

                                        <Form.Item
                                            label={t`Montant`}
                                            name={"montant"}
                                            rules={[{required: true, message: t`Ce champ est obligatoire`}]}
                                        >
                                            <InputNumber size="large" min={forceMonthNb!==undefined?0:1} style={{width: "100%"}} addonAfter={currencyAfterField()} disabled={campagneData?.config?.fixedMontant || isStockable(campagneData)} readOnly={campagneData?.config?.fixedMontant || isStockable(campagneData)}/>
                                        </Form.Item>

                                        {/* Étalement et mensualités n'ont pas de sens pour une promesse (ils étaient ignorés en silence). */}
                                        {montant >= maxDivide && !productMonth && maxDivide!==1 && e.getFieldValue("typeMethod")!=="PROMESSE" &&(
                                            <Form.Item name={"divide"} style={{width: "100%"}}>
                                                <select className={"form-control"} style={{width: "100%", marginBottom: 10, marginTop: -10}}>
                                                    <option value={0}>⬇️ {monthlyAmountDisplay ? t`Donner en une seule fois` : t`Ne pas diviser mon don`}</option>
                                                    {Array.from(Array(maxDivide-1).keys()).map((i) => {
                                                        const nb=i + 2
                                                        // Arrondi au supérieur : c'est la mensualité réellement prélevée (cf. tunnel de paiement).
                                                        if(monthlyAmountDisplay){
                                                            const perMonth=currencyFormater(Math.ceil(montant / nb),0,selectedDevise)
                                                            return <option key={i} value={nb}>{t`${perMonth} /mois pendant ${nb} mois`}</option>
                                                        }
                                                        return <option key={i} value={nb}>{t`Diviser mon don sur ${i + 2} mois soit`} ({Math.ceil(montant / nb)} {t`/ mois`})</option>
                                                    })}
                                                </select>
                                            </Form.Item>
                                        )}

                                        {productMonth && e.getFieldValue("typeMethod")!=="PROMESSE" &&(
                                            <Form.Item name={"nbMonth"} style={{width: "100%"}}>
                                                <select className={"form-control"} style={{width: "100%", marginBottom: 10, marginTop: -10}} disabled={forceMonthNb!==undefined}>
                                                    <option value={0}>{t`En continue - résiliable à tout moment`}</option>
                                                    {Array.from(Array(maxNbMonth-1).keys()).map((i) => {
                                                        const value=i + 2
                                                        if(minNbMonth && minNbMonth>value){
                                                            return null
                                                        }
                                                        return(
                                                            <option key={i} value={value}>{t`Pendant ${value} mois`}</option>
                                                        )
                                                    }).reverse()}
                                                </select>
                                            </Form.Item>
                                        )}

                                        {Array.isArray(campagneData?.teams) && campagneData.teams.length>0 && campagneData?.config?.modalHideTeam!==true && (
                                            <Form.Item
                                                label={t`Equipe`}
                                                name={"team"}
                                            >
                                                <select className={"form-control"} placeholder={t`Equipe`}>
                                                    <option value={""}>{t`Sans Equipe`}</option>
                                                    {campagneData?.teams.filter(i=>!i.hidden).map(i => (
                                                        <option key={i.param} value={i.param}>{i.label}</option>))}
                                                </select>
                                            </Form.Item>
                                        )}

                                        {campagneData?.gala && !campagneData?.config?.noPromesse &&(
                                            <Fragment>

                                                <Form.Item
                                                    label={t`Je souhaite régler mon don :`}
                                                    name={"typeMethod"}
                                                >
                                                    <Radio.Group size={"large"}>
                                                        <Radio.Button value={"PAIEMENT"}><Trans>Maintenant</Trans></Radio.Button>
                                                        <Radio.Button value={"PROMESSE"}><Trans>Promesse de don</Trans></Radio.Button>
                                                    </Radio.Group>
                                                </Form.Item>

                                                <Form.Item noStyle shouldUpdate>
                                                    {(form)=>{

                                                        if(form.getFieldValue("typeMethod")!=="PROMESSE") {
                                                            return false
                                                        }

                                                        return(
                                                            <div style={{marginTop:30}}>

                                                                <div style={{display: !isMobile ? "flex" : "block", justifyContent: "space-between"}}>
                                                                    <Form.Item
                                                                        label={t`Prénom`}
                                                                        name="prenom"
                                                                        rules={[{ required: true, message: t`Ce champ est obligatoire` }]}
                                                                        style={{width:!isMobile ? "48%" : "100%"}}
                                                                    >
                                                                        <Input placeholder={t`Prénom`} autoComplete="given-name" />
                                                                    </Form.Item>

                                                                    <Form.Item
                                                                        label={t`Nom`}
                                                                        name="nom"
                                                                        rules={[{ required: true, message: t`Ce champ est obligatoire` }]}
                                                                        style={{width:!isMobile ? "48%" : "100%"}}
                                                                    >
                                                                        <Input placeholder={t`Nom`} autoComplete="family-name"/>
                                                                    </Form.Item>
                                                                </div>

                                                                <div style={{display: !isMobile ? "flex" : "block", justifyContent: "space-between"}}>
                                                                    <Form.Item
                                                                        label={t`Email`}
                                                                        name="email"
                                                                        rules={[{ required: true, message: t`Ce champ est obligatoire` },{ type: "email", message: t`Cette adresse email n'est pas valide` }]}
                                                                        style={{width:!isMobile ? "48%" : "100%"}}
                                                                    >
                                                                        <Input type="email" placeholder={t`Email`} autoComplete="email" />
                                                                    </Form.Item>

                                                                    <Form.Item
                                                                        label={t`Téléphone`}
                                                                        name="tel"
                                                                        rules={[{ required: true, message: t`Ce champ est obligatoire` }]}
                                                                        style={{width:!isMobile ? "48%" : "100%"}}
                                                                    >
                                                                        <PhoneInput type="tel" placeholder={t`Téléphone`} autoComplete="tel" />
                                                                    </Form.Item>
                                                                </div>

                                                                <Form.Item
                                                                    label={t`Nom qui apparaitra`}
                                                                    name={"displayName"}
                                                                >
                                                                    <Input placeholder={t`Vide = "Anonyme"`} autoComplete="given-name family-name"/>
                                                                </Form.Item>
                                                            </div>
                                                        )
                                                    }}
                                                </Form.Item>
                                            </Fragment>
                                        )}

                                    </Fragment>
                                )
                            }}
                        </Form.Item>

                        <Form.Item noStyle shouldUpdate>
                            {/* En mode promesse rien n'est débité : la bannière « Vous serez débité de X » mentait. */}
                            {(f)=> f.getFieldValue("typeMethod")==="PROMESSE" ? null : renderCurrencyBanner(f.getFieldValue("montant"),f.getFieldValue("divide"))}
                        </Form.Item>

                        <Form.Item style={{display:"flex",justifyContent:"flex-end",marginTop:20}}>
                            <Button disabled={loading} className={"modal-submit-button"} htmlType={"submit"} size={"large"}>
                                <Trans>Valider</Trans>
                            </Button>
                        </Form.Item>

                    </Form>
                    )}
                </div>
            </Container>
        </ConfigProvider>
    )
}

function ContainerNoModal(props)
{
    return(
        <div className={"container"} {...props}/>
    )
}
